import { AnchorProvider, Program } from "@coral-xyz/anchor";
import { TOKEN_PROGRAM_ID } from "@solana/spl-token";
import type { Connection, PublicKey, Signer } from "@solana/web3.js";
import { SystemProgram, SYSVAR_CLOCK_PUBKEY, SYSVAR_RENT_PUBKEY } from "@solana/web3.js";
import type { TulipVault } from "@stepfinance/vaults";
import { RAYDIUM_TULIP_VAULT_PROGRAM_ID, TULIP_DECIMALS, TULIP_TOKEN_MINT } from "@stepfinance/vaults";
import { TulipRaydiumVaultJSON } from "@stepfinance/vaults/src/lib/info/tulip/idls/tulip_raydium_vault";

import { confirmOptions } from "../constants/transaction";
import type { ConnectedWallet } from "../hooks/wallet/useConnectedWallet";
import { sendTransaction } from "../utils/transactions";
import { createInstructionsToTakeFee } from "./fee";

export const harvest = async (
  connection: Connection,
  wallet: ConnectedWallet,
  vaultInfo: TulipVault,
  rewardAccount: PublicKey,
  pdaAssociatedTulipAddress: PublicKey,
  awaitConfirmation = true,
): Promise<string> => {
  const signers: Signer[] = [];
  const provider = new AnchorProvider(connection, wallet, { commitment: "confirmed" });
  const program = new Program(TulipRaydiumVaultJSON, RAYDIUM_TULIP_VAULT_PROGRAM_ID, provider);

  const { metaNonce, nonce, rewardNonce } = vaultInfo.harvest;
  const harvestIx = await program.methods
    .harvestTulips({
      metaNonce,
      nonce,
      rewardNonce,
    })
    .accounts({
      authority: wallet.publicKey,
      vault: vaultInfo.vault.address,
      vaultPdaAccount: vaultInfo.pda,
      userInfoAccount: vaultInfo.vault.infoAccount,
      userBalanceAccount: vaultInfo.key,
      userTulipRewardMetadata: vaultInfo.userRewardMetadataAccount,
      userTulipTokenAccount: rewardAccount,
      vaultTulipTokenAccount: pdaAssociatedTulipAddress,
      tokenProgramId: TOKEN_PROGRAM_ID,
      clock: SYSVAR_CLOCK_PUBKEY,
      rent: SYSVAR_RENT_PUBKEY,
      systemProgram: SystemProgram.programId,
    })
    .instruction();

  const { createAccountInstructions, feeInstructions } = await createInstructionsToTakeFee(
    connection,
    wallet,
    TULIP_TOKEN_MINT,
    TULIP_DECIMALS,
    vaultInfo.pendingReward,
    rewardAccount,
  );

  return await sendTransaction(
    connection,
    wallet,
    [harvestIx, ...createAccountInstructions, ...feeInstructions],
    signers,
    awaitConfirmation,
    confirmOptions,
    true,
  );
};
